/**
 * CruxLogo.tsx
 *
 * CRUX RE brand logo — aligned with property management platform branding.
 * Diamond icon (gold) + "CRUX" (Russo One) + rotated outlined "RE" (gold stroke).
 */


interface CruxLogoProps {
  /** Variant: 'horizontal' for header, 'stacked' for full display */
  variant?: 'horizontal' | 'stacked';
  /** Size of the logo */
  size?: 'sm' | 'md' | 'lg';
  /** Show the tagline */
  showSubtitle?: boolean;
  /** Color theme */
  theme?: 'light' | 'dark';
  /** Additional CSS classes */
  className?: string;
}

const sizeMap = {
  sm: { icon: 28, text: '16px', re: '13px', reStroke: '1px', subtitle: 'text-[7px]', stroke: 2.5 },
  md: { icon: 36, text: '22px', re: '17px', reStroke: '1.5px', subtitle: 'text-[9px]', stroke: 2.5 },
  lg: { icon: 48, text: '32px', re: '24px', reStroke: '2px', subtitle: 'text-[11px]', stroke: 2.5 },
};

/**
 * CRUX RE diamond logo icon — gold on dark, black on light
 */
function CruxIcon({ size, color }: { size: number; color: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M50 8 L92 50 L50 92 L8 50 Z" fill="none" stroke={color} strokeWidth="2.5" />
      <path d="M50 28 L72 50 L50 72 L28 50 Z" fill="none" stroke={color} strokeWidth="2.5" />
      <path d="M50 8 L92 50 L72 50 L50 28 Z" fill={color} />
      <path d="M92 50 L50 92 L50 72 L72 50 Z" fill={color} />
      <line x1="50" y1="8" x2="50" y2="28" stroke={color} strokeWidth="2.5" />
      <line x1="8" y1="50" x2="28" y2="50" stroke={color} strokeWidth="2.5" />
      <line x1="50" y1="72" x2="50" y2="92" stroke={color} strokeWidth="2.5" />
    </svg>
  );
}

export function CruxLogo({
  variant = 'horizontal',
  size = 'md',
  showSubtitle = true,
  theme = 'dark',
  className = '',
}: CruxLogoProps) {
  const s = sizeMap[size];
  const gold = '#d4a853';
  const iconColor = gold;
  const textColor = theme === 'dark' ? '#ffffff' : '#1c1917';
  const subtitleColor = theme === 'dark' ? 'text-stone-500' : 'text-stone-500';

  if (variant === 'stacked') {
    return (
      <div className={`flex flex-col items-center gap-1 ${className}`}>
        <CruxIcon size={s.icon * 2} color={iconColor} />
        <div className="flex items-center gap-1">
          <span
            style={{
              fontFamily: '"Russo One", sans-serif',
              fontSize: s.text,
              lineHeight: 1,
              color: textColor,
              letterSpacing: '0.05em',
            }}
          >
            CRUX
          </span>
          <span
            style={{
              fontFamily: '"Russo One", sans-serif',
              fontSize: s.re,
              lineHeight: 1,
              transform: 'rotate(45deg)',
              WebkitTextStroke: `${s.reStroke} ${gold}`,
              WebkitTextFillColor: 'transparent',
              display: 'inline-block',
            }}
          >
            RE
          </span>
        </div>
        {showSubtitle && (
          <span className={`${subtitleColor} ${s.subtitle} font-semibold tracking-[0.12em]`}>
            ACQUISITIONS INTELLIGENCE
          </span>
        )}
      </div>
    );
  }

  // Horizontal lockup (default)
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <CruxIcon size={s.icon} color={iconColor} />
      <div className="flex flex-col">
        <div className="flex items-center gap-1">
          <span
            style={{
              fontFamily: '"Russo One", sans-serif',
              fontSize: s.text,
              lineHeight: 1,
              color: textColor,
              letterSpacing: '0.05em',
            }}
          >
            CRUX
          </span>
          <span
            style={{
              fontFamily: '"Russo One", sans-serif',
              fontSize: s.re,
              lineHeight: 1,
              transform: 'rotate(45deg)',
              WebkitTextStroke: `${s.reStroke} ${gold}`,
              WebkitTextFillColor: 'transparent',
              display: 'inline-block',
            }}
          >
            RE
          </span>
        </div>
        {showSubtitle && (
          <span className={`${subtitleColor} ${s.subtitle} font-semibold tracking-[0.1em]`}>
            ACQUISITIONS INTELLIGENCE
          </span>
        )}
      </div>
    </div>
  );
}

export default CruxLogo;
